export const BENCHMARK_PAGE_SIZE = 50
export const COMPANY_QUERY_LIMIT = 200
export const LOAD_ERROR = "The audit data could not be loaded."
export const COMPANY_TOO_LONG = "company must be at most 200 characters"
export const EMPTY_VALUE = "—"
export const LOADING_VALUE = "…"

const FAST_REPLY_SECONDS = 60
const FIVE_MINUTE_SECONDS = 300

export function readBenchmarkError(payload) {
  if (typeof payload?.detail === "string" && payload.detail.trim()) return payload.detail.trim()
  return LOAD_ERROR
}

export function uniqueCampaignIds(mappings) {
  const seen = new Set()
  const ids = []
  for (const mapping of mappings || []) {
    const id = typeof mapping?.campaign_id === "string" ? mapping.campaign_id.trim() : ""
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

export function uniqueCountries(mappings) {
  const seen = new Set()
  const countries = []
  for (const mapping of mappings || []) {
    const country = typeof mapping?.country === "string" ? mapping.country.trim() : ""
    if (!country) continue
    const key = country.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    countries.push(country)
  }
  return countries
}

export function leadStatus(seconds) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) {
    return { tone: "red", label: "No reply" }
  }
  if (seconds < FAST_REPLY_SECONDS) return { tone: "green", label: "Under 60 seconds" }
  if (seconds <= FIVE_MINUTE_SECONDS) return { tone: "yellow", label: "1 to 5 minutes" }
  return { tone: "red", label: "Over 5 minutes" }
}

function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"}`
}

export function formatResponseTime(seconds) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return "No reply"
  const total = Math.floor(seconds)
  if (total < FAST_REPLY_SECONDS) return plural(total, "second")
  if (total < 3600) {
    const minutes = Math.floor(total / 60)
    const remainder = total % 60
    if (remainder === 0) return plural(minutes, "minute")
    return `${plural(minutes, "minute")} ${plural(remainder, "second")}`
  }
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

function cleanText(value) {
  if (typeof value !== "string") return ""
  return value.trim()
}

export function formatCompanyName(name) {
  return cleanText(name) || EMPTY_VALUE
}

export function formatLocation(lead) {
  const city = cleanText(lead?.city)
  const state = cleanText(lead?.state)
  const country = cleanText(lead?.country)
  const place = [city, state, country].filter(Boolean).join(", ")
  return place || EMPTY_VALUE
}

export function mapLead(lead) {
  const status = leadStatus(lead?.response_time_seconds)
  const website = cleanText(lead?.website)
  return {
    id: lead?.id,
    name: formatCompanyName(lead?.company_name),
    location: formatLocation(lead),
    website: website || null,
    responseLabel: formatResponseTime(lead?.response_time_seconds),
    statusLabel: status.label,
    statusTone: status.tone,
  }
}

export function formatPercent(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return EMPTY_VALUE
  const digits = value < 1 ? 3 : 1
  return `${value.toFixed(digits)}%`
}

export function formatCompanyCount(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return EMPTY_VALUE
  const digits = Math.trunc(value).toString()
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export function countLabel(statsStatus, total) {
  if (statsStatus === "loading") return LOADING_VALUE
  if (statsStatus === "ready") return formatCompanyCount(total)
  return EMPTY_VALUE
}

export function filterFromId(id) {
  if (id === "fast" || id === "slow") return { kind: "speed", speed: id }
  if (typeof id === "string" && id.startsWith("country:")) {
    return { kind: "country", country: id.slice("country:".length) }
  }
  return { kind: "all" }
}

export function buildLeaderboardSearchBody(filterId, page) {
  const body = { page, page_size: BENCHMARK_PAGE_SIZE }
  const filter = filterFromId(filterId)
  if (filter.kind === "country") return { ...body, countries: [filter.country] }
  if (filter.kind === "speed") return { ...body, response_speeds: [filter.speed] }
  return body
}

export function buildCompanySearchBody(company, page) {
  return { company: company.trim(), page, page_size: BENCHMARK_PAGE_SIZE }
}

export function companyQueryError(query) {
  if (query.trim().length > COMPANY_QUERY_LIMIT) return COMPANY_TOO_LONG
  return ""
}
